import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import './EducationSection.scss';

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, isPercentage = false }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{
        backgroundColor: '#fff',
        padding: '8px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 500, color: '#1a202c' }}>{label}</p>
        <p style={{ margin: '4px 0 0 0', color: '#4a5568' }}>
          {isPercentage 
            ? `${payload[0].value}%`
            : `Count: ${payload[0].value}`}
        </p>
      </div>
    );
  }
  return null;
};

// Custom tick component with ellipsis for long text
const CustomXAxisTick = ({ x, y, payload, width }) => {
  const maxLength = 12; // Maximum characters before truncation
  const text = payload.value;
  const displayText = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#666"
        fontSize={11}
        fontWeight="500"
        transform="rotate(-45)"
        style={{ fontSize: '11px', fontWeight: '500' }}
      >
        {displayText}
      </text>
    </g>
  );
};

const EducationSection = ({ locumHoursData }) => {
  // const qualificationData = [
  //   { name: 'Diploma', value: 150 },
  //   { name: "Bachelor's", value: 400 },
  //   { name: "Master's", value: 300 },
  //   { name: 'Doctorate', value: 100 },
  //   { name: 'Other', value: 50 }
  // ];
  const qualificationData = locumHoursData?.topQualificationsGraph?.map(item => ({
    name: item.qualification,
    value: item.count
  })) || [];
  const specializationData = locumHoursData?.topSpecializationsGraph?.map((item, index) => {
    const colors = ['#034585', '#10B981', '#F97316', '#8B5CF6', '#EF4444'];
    return {
      name: item.specialization,
      value: Math.round(item.percentage), // Convert to percentage
      color: colors[index % colors.length] // Cycle through colors
    };
  }) || [];

  // const specializationData = [
  //   { name: 'General Med', value: 95, color: '#034585' },
  //   { name: 'Emergency', value: 85, color: '#10B981' },
  //   { name: 'Pediatrics', value: 75, color: '#F97316' },
  //   { name: 'Surgery', value: 65, color: '#8B5CF6' },
  //   { name: 'Psychiatry', value: 55, color: '#EF4444' }
  // ];

  // Calculate max value for Y-axis with nice rounded steps
  const calculateYAxisTicks = (data) => {
    if (!data || data.length === 0) return { max: 10, ticks: [0, 10, 20, 30, 40, 50] };
    
    const maxValue = Math.max(...data.map(item => item.value));
    const step = Math.pow(10, Math.floor(Math.log10(maxValue)));
    let maxTick = Math.ceil(maxValue / step) * step;
    
    // Ensure we have at least 5 ticks
    const tickCount = 5;
    const tickStep = Math.max(1, Math.ceil(maxTick / (tickCount - 1) / step) * step);
    maxTick = tickStep * (tickCount - 1);
    
    const ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      ticks.push(i * tickStep);
    }
    
    return { max: maxTick, ticks };
  };

  const yAxisConfig = calculateYAxisTicks(qualificationData);

  return (
    <div className="education">
      <h2 className="education__title">Education & Skills</h2>
      
      <div className="education__grid">
        {/* Highest Qualification - Vertical Bar Chart */}
        <div className="education__card">
          <h3 className="education__card-title">Highest Qualification</h3>
          <p className="education__card-subtitle">Educational background distribution of locum professionals</p>
          <div className="education__chart">
          {qualificationData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={qualificationData} 
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                barCategoryGap={qualificationData.length === 1 ? '20%' : '30%'}
              >
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={<CustomXAxisTick />}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={0}
                  padding={{ left: qualificationData.length === 1 ? 20 : 0, right: qualificationData.length === 1 ? 20 : 0 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  // tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  tick={{ fontWeight:'500', fontSize: 11  }}
                  domain={[0, yAxisConfig.max]}
                  ticks={yAxisConfig.ticks}
                />
                <Tooltip 
                  content={<CustomTooltip />} 
                  cursor={{ fill: 'rgba(255, 253, 253, 0)' }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#034585" 
                  radius={[2, 2, 0, 0]}
                  maxBarSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="education__no-data">There is no educational background data available to display.</div>
          )}
          </div>
        </div>

        {/* Top 5 Specializations - Horizontal Bar Chart */}
        <div className="education__card">
          <h3 className="education__card-title">Top 5 Specializations</h3>
          <p className="education__card-subtitle">Most in-demand locum specializations</p>
          <div className="education__chart education__chart--horizontal">
          {specializationData.length > 0 ? (
            <div className="education__horizontal-bars">
              {specializationData.map((item, index) => (
                <div key={index} className="education__horizontal-bar-item">
                
                <div className="education__horizontal-bar-line">
                    <div className="education__horizontal-bar-label">
                    {item.name}
                    </div>
                    <div className="education__horizontal-bar-percentage">
                    {item.value}%
                  </div>
                  </div>
                  <div className="education__horizontal-bar-container">
                    <div 
                      className="education__horizontal-bar-fill"
                      style={{ 
                        width: `${item.value}%`,
                        backgroundColor: item.color
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="education__no-data">There are no specializations recorded for the selected criteria.</div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationSection;